// Client-side PDF path for the alignment essay — a sibling of
// coverLetterToPrint.ts (the SAME popup + window.print() pattern: clean
// print-only window, browser print dialog -> Save as PDF). The essay is
// plain first-person prose exactly like a cover letter (no themed DOM node
// to clone, no fixed recipient/address block to invent), so this file mirrors
// coverLetterToPrint.ts's own tiny from-scratch stylesheet rather than
// introducing a third print-CSS variant for what is structurally the same
// document shape.

const PRINT_SAFE_INK = '#1c1c1c';

/**
 * Build the print document's full HTML string. Pure (no DOM, no window) —
 * split out so a future test can pin the escaping/name-block branches
 * directly, mirroring coverLetterToPrint.ts's buildCoverLetterPrintHtml.
 * `applicantName` is trimmed and omitted entirely when blank/undefined —
 * never rendered as a placeholder.
 */
export function buildAlignmentEssayPrintHtml(
  essayText: string,
  applicantName?: string | null,
  documentTitle = 'Alignment essay',
): string {
  const trimmedName = applicantName?.trim();
  const nameBlock = trimmedName ? `<div class="ae-name">${escapeHtml(trimmedName)}</div>` : '';
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(
    documentTitle,
  )}</title><style>${ESSAY_CSS}</style></head><body><div class="ae-doc">${nameBlock}<div class="ae-body">${escapeHtml(essayText)}</div></div></body></html>`;
}

/**
 * Print the given alignment essay text. Opens a print-only window with a
 * plain layout: an optional applicant name line, then the body verbatim
 * (whitespace preserved). Returns false if the popup was blocked — the
 * caller reports that honestly (never a silent no-op), same contract as
 * printCoverLetter/printResume.
 */
export function printAlignmentEssay(
  essayText: string,
  applicantName?: string | null,
  documentTitle = 'Alignment essay',
): boolean {
  const win = window.open('', '_blank', 'width=900,height=1200');
  if (!win) return false;

  win.document.open();
  win.document.write(buildAlignmentEssayPrintHtml(essayText, applicantName, documentTitle));
  win.document.close();

  win.focus();
  win.onafterprint = () => win.close();
  setTimeout(() => {
    win.print();
  }, 350);
  return true;
}

const ESSAY_CSS = `
html, body { margin: 0; padding: 0; background: #fff; }
.ae-doc {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
  font-size: 11pt; line-height: 1.55; color: ${PRINT_SAFE_INK};
  width: 210mm; min-height: 297mm; box-sizing: border-box;
  padding: 22mm 22mm; margin: 0 auto;
}
.ae-name { font-size: 13pt; font-weight: 700; margin-bottom: 18px; }
.ae-body { white-space: pre-wrap; overflow-wrap: anywhere; }
@media print {
  @page { size: A4; margin: 0; }
  .ae-doc { width: 100%; min-height: auto; padding: 18mm; }
}
`;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;',
  );
}
